/**
 * <TutorCloseOutStudentCard> — la carte d'un élève dans le bilan de séance du masteur (organism).
 *
 * L'écran d'aujourd'hui (`StudentCloseOutCard`), à parité, en trois formes :
 *  - **absent** : la ligne d'un élève absent, celle de la prescription (`<TutorAbsentStudentLine>`) —
 *    « élève absent·e — pas de compte-rendu à faire » ;
 *  - **à compléter** : l'en-tête (visage, prénom, classe, et les chips « N points d'attention » /
 *    « N alertes »), le score d'engagement (`<TutorEngagementBar>`), les axes qui ne sont pas OK
 *    et leurs alertes repliables, « Travail Sam : N exercices envoyés », la note et ses étiquettes
 *    (`<LiveRating>`), le compte-rendu (`<TutorCommentField>`), puis « Terminé pour Jules », grisé
 *    tant que la note ou le mot manque, avec la phrase qui dit quoi ;
 *  - **faite** : une ligne repliée — « Bilan fait · Jules · le début du mot » et ses chips —, qui
 *    se déplie pour reprendre la carte.
 * Aucune ne porte de liseré : chaque carte en avait un (l'univers à compléter, le vert une fois
 * faite), il ne distinguait rien que la forme repliée et « Bilan fait » ne disent déjà.
 *
 * Contrôlée : la note, les étiquettes, le mot, son envoi et l'état « fait » arrivent en props. Seul
 * le dépliage d'une carte faite vit ici (`defaultExpanded` pour une story).
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Avatar } from "../../atoms/Avatar"
import { Badge } from "../../atoms/Badge"
import { Button } from "../../atoms/Buttons/Button"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Divider } from "../../atoms/Divider"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { textInkOnFill } from "../../theme/tokens/colors"
import { Card } from "../../molecules/Card"
import { TutorAbsentStudentLine } from "../../molecules/TutorAbsentStudentLine"
import { TutorEngagementBar } from "../../molecules/TutorEngagementBar"
import { QUALITY_STATUS } from "../../molecules/TutorQualityAxisCard"
import { TutorQualityAlerts } from "../../molecules/TutorQualityAlerts"
import { LiveRating } from "../LiveRating"
import { TutorCommentField, type TutorCommentFieldProps } from "../TutorCommentField"
import {
    type UITutorCloseOutPresentStudent,
    type UITutorCloseOutStudent,
    type UITutorQualityAxis,
} from "../../../ports/primary/ui/types/tutorCloseOut"
import { countLabel } from "../../../ports/primary/ui/formatters/textFormatter"

/** Les gestes du mot, relayés tels quels au champ. */
type CommentHandlers = Pick<
    TutorCommentFieldProps,
    | "onChangeMode"
    | "onChangeText"
    | "onRecordVoice"
    | "recorder"
    | "onDeleteVoice"
    | "onToggleVoicePlay"
    | "voicePlaying"
    | "voiceProgress"
    | "onRetry"
>

export type TutorCloseOutStudentCardProps = CommentHandlers & {
    student: UITutorCloseOutStudent
    onRate: (rating: number) => void
    onToggleTag: (tag: string) => void
    /** « Terminé pour Jules ». */
    onDone: () => void
    /** Une carte faite s'ouvre dépliée. Défaut `false` : la ligne repliée. */
    defaultExpanded?: boolean
    testID?: string
}

const AVATAR = 36
const PREVIEW_MAX = 70

/** Ce qui manque pour valider — les phrases de l'écran d'aujourd'hui. */
function closeOutHelperText(student: UITutorCloseOutPresentStudent): string | null {
    const hasRating = student.rating.rating > 0
    const hasContent = hasCommentContent(student)
    if (!hasRating && !hasContent) return "Ajoute une note ⭐ et un commentaire pour valider"
    if (!hasRating) return "Ajoute une note ⭐ pour valider"
    if (!hasContent) return "Ajoute un commentaire (texte ou vocal) pour valider"
    if (student.comment.send === "sending") return "Envoi du vocal…"
    if (student.comment.send === "failed") return "Ton commentaire n'est pas encore enregistré"
    return null
}

function hasCommentContent(student: UITutorCloseOutPresentStudent): boolean {
    return student.comment.text.trim() !== "" || student.comment.voice != null
}

/** L'élève est venu : sa carte de bilan. */
export function isPresent(student: UITutorCloseOutStudent): student is UITutorCloseOutPresentStudent {
    return student.absent === false
}

function commentPreview(student: UITutorCloseOutPresentStudent): string {
    const text = student.comment.text.trim()
    if (text !== "") return text.length > PREVIEW_MAX ? `${text.slice(0, PREVIEW_MAX).trimEnd()}…` : text
    if (student.comment.voice != null) return "Compte-rendu vocal enregistré"
    return ""
}

/** Une chip par alerte, pas par axe : les manquements et les points d'attention se comptent à part. */
function SeverityBadges(props: { axes: UITutorQualityAxis[] }) {
    const alerts = props.axes.flatMap((axis) => axis.alerts)
    const breaches = alerts.filter((alert) => alert.charterBreach).length
    const attentions = alerts.length - breaches
    if (alerts.length === 0) return null
    return (
        <Row gap="1" style={styles.wrap}>
            {attentions > 0 && (
                <Badge
                    text={countLabel(attentions, "point d'attention", "points d'attention")}
                    variant="warning"
                    tinted
                    iconName="exclamation-triangle"
                />
            )}
            {breaches > 0 && (
                <Badge text={countLabel(breaches, "alerte")} variant="danger" tinted iconName="exclamation-circle" />
            )}
        </Row>
    )
}

export function TutorCloseOutStudentCard(props: TutorCloseOutStudentCardProps) {
    const { student, onRate, onToggleTag, onDone, defaultExpanded = false, testID, ...commentHandlers } = props
    const [expanded, setExpanded] = React.useState(defaultExpanded)

    if (!isPresent(student)) {
        return (
            <TutorAbsentStudentLine
                name={student.firstname}
                avatarUrl={student.avatarUrl}
                detail="pas de compte-rendu à faire"
                testID={testID}
            />
        )
    }

    const { firstname, done, engagement, alertAxes, samActivitiesCount, rating, comment } = student
    const helper = closeOutHelperText(student)
    const showBody = !done || expanded

    const header = done ? (
        <Pressable
            onPress={() => setExpanded((value) => !value)}
            accessibilityRole="button"
            accessibilityState={{ expanded }}
            style={styles.doneStrip(expanded)}
        >
            <Row gap="2" align="center">
                <Avatar uri={student.avatarUrl} name={firstname} size={30} />
                <Icon name="check-circle" size={16} color={textInkOnFill("success")} />
                <Stack gap="1" style={styles.shrink}>
                    <Text size="text-sm" numberOfLines={1}>
                        <Text size="text-sm" weight="bold">
                            Bilan fait
                        </Text>
                        {` · ${firstname}`}
                        {commentPreview(student) !== "" ? ` · ${commentPreview(student)}` : ""}
                    </Text>
                    <SeverityBadges axes={alertAxes} />
                </Stack>
                <Icon name={expanded ? "chevron-up" : "chevron-down"} size={16} color="text-muted" />
            </Row>
        </Pressable>
    ) : (
        <Row gap="3" align="center">
            <Avatar uri={student.avatarUrl} name={firstname} size={AVATAR} />
            <Stack gap="1" style={styles.shrink}>
                <Stack gap="0">
                    <Text size="text-md" weight="bold">
                        {firstname}
                    </Text>
                    {student.className != null && (
                        <Text size="text-xs" color="text-muted">
                            {student.className}
                        </Text>
                    )}
                </Stack>
                <SeverityBadges axes={alertAxes} />
            </Stack>
        </Row>
    )

    return (
        <Card p="0" testID={testID}>
            <View style={done ? undefined : styles.head}>{header}</View>
            {showBody && (
                <Stack gap="3" style={styles.body}>
                    {engagement != null && <TutorEngagementBar engagement={engagement} />}
                    {alertAxes.map((axis, index) => (
                        <Stack key={index} gap="2">
                            <Row gap="2" align="center">
                                <Icon
                                    name={QUALITY_STATUS[axis.status].icon}
                                    size={16}
                                    color={textInkOnFill(QUALITY_STATUS[axis.status].color)}
                                />
                                <Text size="text-sm" weight="semibold">
                                    {axis.name}
                                </Text>
                            </Row>
                            <View style={styles.indent}>
                                <TutorQualityAlerts alerts={axis.alerts} collapsible />
                            </View>
                        </Stack>
                    ))}
                    {samActivitiesCount != null && (
                        <Row gap="1" align="center">
                            <Icon name="check-circle" size={16} color={textInkOnFill("success")} />
                            <Text size="text-xs" weight="bold" color={textInkOnFill("success")}>
                                {`Travail Sam : ${countLabel(samActivitiesCount, "exercice envoyé", "exercices envoyés")}`}
                            </Text>
                        </Row>
                    )}
                    <Divider />
                    <Stack gap="3" align="center">
                        <Text size="text-md" weight="bold" color="primary" center>
                            {`Qu'as-tu pensé de ce live avec ${firstname} ?`}
                        </Text>
                        <LiveRating
                            rating={rating.rating > 0 ? rating.rating : null}
                            onRate={onRate}
                            subtitle={rating.tagsTitle}
                            comments={rating.tags}
                            selectedComments={rating.selectedTags}
                            onToggleComment={onToggleTag}
                        />
                    </Stack>
                    <Divider />
                    <TutorCommentField
                        comment={comment}
                        title={`Fais un compte-rendu pour ${firstname}`}
                        subtitle="Il sera utilisé pour rédiger un bilan de session aux parents"
                        placeholder="Attitude, difficultés, progrès, points forts…"
                        untranscribedMessage={`Ton vocal pour ${firstname} est arrivé vide : on n'a rien pu en tirer. Enregistre-le de nouveau, ou écris-le.`}
                        {...commentHandlers}
                    />
                    <Stack gap="2">
                        <Button
                            label={`Terminé pour ${firstname}`}
                            iconName="check"
                            color="success"
                            disabled={helper != null}
                            onPress={() => {
                                setExpanded(false)
                                onDone()
                            }}
                            fullWidth
                        />
                        {helper != null && (
                            <Text size="text-xs" color="text-muted" center>
                                {helper}
                            </Text>
                        )}
                    </Stack>
                </Stack>
            )}
        </Card>
    )
}

const styles = StyleSheet.create((theme) => ({
    shrink: {
        flex: 1,
        flexShrink: 1,
    },
    wrap: {
        flexWrap: "wrap",
    },
    head: {
        paddingTop: theme.spacing["3"],
        paddingHorizontal: theme.spacing["4"],
    },
    doneStrip: (expanded: boolean) => ({
        paddingVertical: theme.spacing["3"],
        paddingHorizontal: theme.spacing["3"],
        backgroundColor: theme.colors.successLight,
        borderTopRightRadius: theme.radius.lg,
        borderBottomRightRadius: expanded ? 0 : theme.radius.lg,
    }),
    body: {
        paddingTop: theme.spacing["3"],
        paddingHorizontal: theme.spacing["4"],
        paddingBottom: theme.spacing["4"],
    },
    indent: {
        paddingLeft: theme.spacing["6"],
    },
}))
